import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { CopyOutlined } from '@ant-design/icons';
import PasswordGeneratorField from '@components/common/form/PasswordGeneratorField';
import {
    CHANGE_DEVICE_STATUS_CODE,
    DATE_FORMAT_DISPLAY,
    DATE_FORMAT_VALUE,
    DEFAULT_FORMAT,
    STATUS_PENDING,
} from '@constants';
import { deviceStatus } from '@constants/masterData';
import useAuth from '@hooks/useAuth';
import useBasicForm from '@hooks/useBasicForm';
import useTranslate from '@hooks/useTranslate';
import { BaseForm, DatePickerField, SelectField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { copyToClipboard, generateSecret, getDefaultPiServerSettings } from '@utils/index';
import { Button, Card, Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

import styles from './index.module.scss';
dayjs.extend(customParseFormat);
dayjs.extend(utc);


const PiServerDeviceForm = ({ formId, actions, dataDetail, onSubmit, setIsChangedFormValues, categories, isEditing }) => {
    const translate = useTranslate();
    const statusValues = translate.formatKeys(deviceStatus, ['label']);
    const { profile } = useAuth();
    
    const permissionCodes = profile?.group?.permissions?.map((permission) => permission.pcode);
    const hasPermissionCode = permissionCodes?.includes(CHANGE_DEVICE_STATUS_CODE);

    const { form, mixinFuncs, onValuesChange } = useBasicForm({
        onSubmit,
        setIsChangedFormValues,
    });

    const apiKeyValue = Form.useWatch('apiKey', form);
    const dbSecretKeyValue = Form.useWatch('dbSecretKey', form);
    const jwtSecretKeyValue = Form.useWatch('jwtSecretKey', form);
    const posSecretKeyValue = Form.useWatch('posSecretKey', form);

    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const parentId = queryParams.get('parentId');
    const params = useParams();

    const handleSubmit = (values) => {
        values.customerId = params.customerId;
        if (values.expireDate) {
            values.expireDate = dayjs(values.expireDate).format(DEFAULT_FORMAT);
        }
        values.settings = isEditing ? dataDetail?.settings : getDefaultPiServerSettings();
        if (parentId) {
            values.parentId = parentId;
            values.type = 2;
        } else {
            values.type = 1;
        }
        const finalValues = {
            ...values,
        };
        return mixinFuncs.handleSubmit(finalValues);
    };

    useEffect(() => {
        dataDetail.expireDate = dataDetail.expireDate && dayjs(dataDetail.expireDate, DATE_FORMAT_VALUE);
        form.setFieldsValue({
            ...dataDetail,
        });
    }, [dataDetail]);

    useEffect(() => {
        if (!isEditing) {
            const defaultValues = {
                status: STATUS_PENDING,
                apiKey: generateSecret(),
                jwtSecretKey: generateSecret(),
                dbSecretKey: generateSecret(),
                posSecretKey: generateSecret(),
            };
            form.setFieldsValue(defaultValues);
        } else {
            if (dataDetail && !dataDetail.apiKey) {
                form.setFieldsValue({
                    apiKey: generateSecret(),
                });
                setIsChangedFormValues(true);
            } else {
                setIsChangedFormValues(false);
            }
        }
    }, [isEditing, dataDetail, hasPermissionCode]);

    return (
        <BaseForm id={formId} onFinish={handleSubmit} form={form} onValuesChange={onValuesChange}>
            <Card className="card-form" bordered={false}>
                <Row gutter={10}>
                    <Col span={12}>
                        <TextField
                            placeholder={translate.formatMessage(commonMessage.placeholderDeviceName)}
                            required
                            label={<FormattedMessage defaultMessage="Device name" />}
                            name="name"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            placeholder={translate.formatMessage(commonMessage.placeholderDeviceId)}
                            required
                            label={<FormattedMessage defaultMessage="Device ID" />}
                            name="deviceId"
                        />
                    </Col>
                </Row>
                <Row gutter={10}>
                    <Col span={12}>
                        <DatePickerField
                            name="expireDate"
                            label={<FormattedMessage defaultMessage="Expiration date" />}
                            format={DATE_FORMAT_DISPLAY}
                            style={{ width: '100%' }}
                            disabledDate={(current) => current && current.isBefore(dayjs().add(1, 'day'), 'day')}
                            // rules={[
                            //     {
                            //         required: true,
                            //         message: translate.formatMessage(messages.requiredExpireDate),
                            //     },
                            //     {
                            //         validator: (_, value) => {
                            //             if (value && value.isBefore(dayjs(), 'day')) {
                            //                 return Promise.reject(translate.formatMessage(messages.expireDateMustBeFuture));
                            //             }
                            //             return Promise.resolve();
                            //         },
                            //     },
                            // ]}
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            placeholder={translate.formatMessage(commonMessage.placeholderIpSocket)}
                            label={<FormattedMessage defaultMessage="IP Socket" />}
                            name="ipSocket"
                            disabled
                        />
                    </Col>
                    <Col span={12}>
                        <PasswordGeneratorField
                            required
                            label={<FormattedMessage defaultMessage="API Key" />}
                            fieldName={'apiKey'}
                            minLength={64}
                            maxLength={64}
                            minLengthMsg={
                                <FormattedMessage defaultMessage={'The length must be exactly 64 characters'} />
                            }
                            maxLengthMsg={
                                <FormattedMessage defaultMessage={'The length must be exactly 64 characters'} />
                            }
                            className={styles.customerInput}
                            value={apiKeyValue}
                            suffix={
                                <Button
                                    onClick={() => {
                                        copyToClipboard(apiKeyValue, translate);                                    }}
                                    className={styles.customerButton}
                                >
                                    <CopyOutlined style={{ alignSelf: 'center' }} />
                                </Button>
                            }
                            disabled={isEditing}
                        />
                    </Col>
                    <Col span={12}>
                        <PasswordGeneratorField
                            required
                            label={<FormattedMessage defaultMessage="Db SecretKey" />}
                            fieldName={'dbSecretKey'}
                            minLength={64}
                            maxLength={64}
                            minLengthMsg={
                                <FormattedMessage defaultMessage={'The length must be exactly 64 characters'} />
                            }
                            maxLengthMsg={
                                <FormattedMessage defaultMessage={'The length must be exactly 64 characters'} />
                            }
                            className={styles.customerInput}
                            value={dbSecretKeyValue}
                            suffix={
                                <Button
                                    onClick={() => {
                                        copyToClipboard(dbSecretKeyValue, translate);                                    }}
                                    className={styles.customerButton}
                                >
                                    <CopyOutlined style={{ alignSelf: 'center' }} />
                                </Button>
                            }
                            disabled={isEditing}
                        />
                    </Col>
                    <Col span={12}>
                        <PasswordGeneratorField
                            required
                            disabled={isEditing}
                            label={<FormattedMessage defaultMessage="Jwt SecretKey" />}
                            fieldName={'jwtSecretKey'}
                            minLength={64}
                            maxLength={64}
                            minLengthMsg={
                                <FormattedMessage defaultMessage={'The length must be exactly 64 characters'} />
                            }
                            maxLengthMsg={
                                <FormattedMessage defaultMessage={'The length must be exactly 64 characters'} />
                            }
                            value={jwtSecretKeyValue}
                            className={styles.customerInput}
                            suffix={
                                <Button
                                    onClick={() => {
                                        copyToClipboard(jwtSecretKeyValue, translate);                                    }}
                                    className={styles.customerButton}
                                >
                                    <CopyOutlined style={{ alignSelf: 'center' }} />
                                </Button>
                            }
                        />
                    </Col>
                    <Col span={12}>
                        <PasswordGeneratorField
                            required
                            disabled={isEditing}
                            label={<FormattedMessage defaultMessage="Pos SecretKey" />}
                            fieldName={'posSecretKey'}
                            minLength={64}
                            maxLength={64}
                            minLengthMsg={
                                <FormattedMessage defaultMessage={'The length must be exactly 64 characters'} />
                            }
                            maxLengthMsg={
                                <FormattedMessage defaultMessage={'The length must be exactly 64 characters'} />
                            }
                            value={posSecretKeyValue}
                            className={styles.customerInput}
                            suffix={
                                <Button
                                    onClick={() => {
                                        copyToClipboard(posSecretKeyValue, translate);                                    }}
                                    className={styles.customerButton}
                                >
                                    <CopyOutlined style={{ alignSelf: 'center' }} />
                                </Button>
                            }
                        />
                    </Col>
                    <Col span={12}>
                        <SelectField
                            required
                            label={<FormattedMessage defaultMessage="Status" />}
                            name="status"
                            options={statusValues}
                            disabled={!hasPermissionCode}
                        />
                    </Col>
                </Row>

                <div className="footer-card-form">{actions}</div>
            </Card>
        </BaseForm>
    );
};

export default PiServerDeviceForm;
